import { createEvent, createSimpleRecord, iEvent, MathExt, runWithTimeout } from 'modules-core/utility'
import { Client, Server, SocketState } from './types'
import { MessageHandlerMap, ReconnectParams } from './types/Server'
import log from 'modules-core/log'
import { eventSystem } from 'modules-core/eventSystem'
import config, { ConfigUpdateEvent } from 'modules-core/config'

const logger = log.getLogger('socketSystem')
const VERSION = '2026-05-20'

export const SocketSystem = () => {
	const BACKOFF_INITIAL = 500  // Half a second
	const BACKOFF_MAX = 60 * 1000  // 1 minute
	const BACKOFF_MULTIPLIER = 2  // Seconds

	let backoff = 0
	let last_connect: number
	let state: SocketState = 'Idle'
	let ws: WebSocket
	const redirectConfigUpdateOrigin = {}

	const onMessageRecord = createSimpleRecord<Server.MessageName, iEvent<Server.MessageParams>>(createEvent)

	const system = {
		get state() { return state },
		set state(_state: SocketState){
			state = _state
			system.onStateChange.invoke(state)
		},

		connect: async (nonce?: string) =>
			new Promise<void>((resolve) => {
				if (system.state === 'Disabled') {
					logger.info('connect() called while disabled')
					resolve()
					return
				}
				system.state = 'Connecting'
				const url = config.websocketUrl
				if (!url) {
					throw Error('Missing WebSocket URL.')
				}
				ws = new WebSocket(url)
				ws.onclose = () => {
					if (system.state === 'Disabled') {
						logger.info('Connection closed while disabled; reconnect suppressed.')
						return
					}
					system.state = 'Idle'
					// Reconnect with progressive backoff. Count the backoff from the last
					// connect, not from the last disconnect.
					backoff = Math.min(
						Math.max(backoff * BACKOFF_MULTIPLIER, BACKOFF_INITIAL),
						BACKOFF_MAX,
					)
					// Subtract current connection duration from backoff.
					backoff -= Math.min(Date.now() - last_connect, backoff) || 0
					logger.info(
						`Connection closed. Reconnect in ${MathExt.millisToSecs(backoff)} seconds.`
					)
					setTimeout(() => system.connect(nonce).then(resolve), backoff)
					// Unset last connect time, because we are disconnected and have already set
					// the backoff timeout. If we successfully connect, we will set it again.
					last_connect = undefined
				}
				ws.onerror = (event) => {
					// Do not reject the promise, because we want to keep retrying.
					logger.error('WebSocket error.', event)
					if (system.state !== 'Error') {
						system.state = 'Error'
					}
					system.onError.invoke(event)
					ws.close()
				}
				ws.onmessage = (event) => {
					if (system.state !== 'Active') {
						system.state = 'Active'
					}
					const data = JSON.parse(event.data) as Server.Message
					logger.debug('<<< Socket Message Received:', data)
					system.onMessage.invoke(data)
					system.onNamedMessage(data.message).invoke(data.params)
					if (data.message === 'registered') {
						resolve()
					}
				}
				ws.onopen = (event) => {
					last_connect = Date.now()
					const message: Client.Message<'register'> = {
						message: 'register',
						session_id: config.sessionId,
						token: config.token,
						version: VERSION,
					}
					if (nonce) {
						message.nonce = nonce
					}
					system.send(message)
				}
			}),

		onMessage:createEvent<Server.Message>(),
		onNamedMessage:onMessageRecord.getOrCreate as
            <T extends Server.MessageName>(key: T) => iEvent<Server.MessageParamsMap[T]>,
		reconnect: () => {
			if (system.state === 'Disabled') {
				logger.info('reconnect() called while disabled - ignoring')
				return
			}
			if (ws !== undefined) {
				if (state === 'Connecting') {
					logger.info('reconnect() called while connecting - ignoring')
					return
				}
				logger.info("reconnect()")
				backoff = 0
				ws.close()
			} else {
				logger.info("reconnect() called before connect()")
			}
		},
		send:<T extends Client.MessageName>(data:Client.Message<T>)=>{
			if (system.state === 'Disabled') {
				logger.info('send() called while disabled - ignoring')
				return
			}
			if (ws === undefined || ws.readyState !== WebSocket.OPEN) {
				logger.warn('send() called while socket is not open - ignoring')
				return
			}
			logger.debug('>>> Socket Message Sent:', data)
			ws.send(JSON.stringify(data))
		},
		disable: () => {
			if (system.state === 'Disabled') {
				logger.info('disable() called while already disabled')
				return
			}
			system.state = 'Disabled'
			if (ws === undefined) {
				logger.info('disable() called before connect()')
				return
			}
			ws.close()
		},
		enable: () => {
			if (system.state !== 'Disabled') {
				logger.info('enable() called while already enabled')
				return
			}
			system.state = 'Idle'
			system.connect()
		},
		onError:createEvent<Event>(),
		onStateChange:createEvent<SocketState>(),
	}

	const socketMessageLookup: MessageHandlerMap = {
		ping: (params) => system.send({ message: 'ping', params }),

		error: (params) => logger.error(params.message),

		reconnect_to_new_backend: async (params: ReconnectParams) => {
			const { game_session_uuid } = params
			const connectionConfig = {
				websocketUrl: params.websocket_url,
				...(game_session_uuid === undefined
					? {}
					: { sessionId: game_session_uuid }),
			}
			logger.info(`Reconnect to new backend: ${connectionConfig.websocketUrl}`)
			const updatePromise = config.update(connectionConfig, {
				origin: redirectConfigUpdateOrigin,
			})
			system.reconnect()
			try {
				await updatePromise
			} catch (error) {
				logger.error('Unable to persist WebSocket configuration.', error)
			}
		},
	}

	Object.entries(socketMessageLookup)
		.forEach(([key,func]:[Server.MessageName,Server.MessageHandler])=>
			system.onNamedMessage(key).addListener(func))

	eventSystem.add('updateConfig', (event: ConfigUpdateEvent) => {
		if (event.configUpdateOrigin === redirectConfigUpdateOrigin) {
			return
		}
		if (
			event.detail.sessionId ||
			event.detail.token ||
			event.detail.websocketUrl
		) {
			system.reconnect()
		}
	})

	return system
}
export type SocketSystem = ReturnType<typeof SocketSystem>
